import { createContext, useContext, type DragEvent } from 'react'

/**
 * Контекст перетаскивания с обязательным запасным путём «тапнул — тапнул».
 *
 * HTML5 drag-and-drop не работает на тач-экранах: на телефоне ученик не смог бы
 * решить ни одно задание с перетаскиванием. Поэтому источник и цель
 * одновременно и draggable, и кликабельны — выбранный тапом элемент ставится
 * следующим тапом по месту.
 */

export type PickerValue = {
  picked: string | null
  pick: (key: string | null) => void
  isPicked: (key: string) => boolean
}

export const PickerContext = createContext<PickerValue | null>(null)

export function useDragPicker(): PickerValue {
  const context = useContext(PickerContext)

  if (context === null) {
    throw new Error('useDragPicker must be used inside DragPickerProvider')
  }

  return context
}

/** Пропсы источника: и мышью, и тапом */
export function sourceProps(key: string, picker: PickerValue, disabled = false) {
  return {
    draggable: !disabled,
    onDragStart: (event: DragEvent) => {
      event.dataTransfer.setData('text/plain', key)
      picker.pick(key)
    },
    onDragEnd: () => picker.pick(null),
    onClick: () => {
      if (disabled) return
      picker.pick(picker.isPicked(key) ? null : key)
    },
  }
}

/** Пропсы цели: принимает и drop, и тап по уже выбранному источнику */
export function targetProps(onDrop: (key: string) => void, picker: PickerValue, disabled = false) {
  return {
    onDragOver: (event: DragEvent) => {
      if (!disabled) event.preventDefault()
    },
    onDrop: (event: DragEvent) => {
      if (disabled) return
      event.preventDefault()
      const key = event.dataTransfer.getData('text/plain')
      if (key) onDrop(key)
      picker.pick(null)
    },
    onClick: () => {
      if (disabled || picker.picked === null) return
      onDrop(picker.picked)
      picker.pick(null)
    },
  }
}
